/**
 * Delta Capture for Google Sheets
 *
 * Captures mini-snapshots of ONLY the cells about to be overwritten
 * before a tool executes. Delegates to Apps Script for cell reading.
 */

import { callAppsScript } from '../agents/sheets-tools'
import type { CaptureStrategy } from './affected-range'
import { getAffectedRanges } from './affected-range'
import type { CellSnapshot, ToolDelta } from './snapshot-db'

/**
 * Get default cell format for delta captures.
 */
function getDefaultFormat() {
  return {
    backgroundColor: null,
    fontColor: null,
    fontBold: false,
    fontItalic: false,
    fontSize: null,
    numberFormat: null,
    horizontalAlignment: null,
    verticalAlignment: null,
    wrapText: false,
  }
}

/**
 * Read cells from specific ranges via Apps Script.
 */
async function readCellsFromRanges(
  ranges: Array<{ sheetName: string | null; address: string }>
): Promise<Record<string, CellSnapshot>> {
  const cells: Record<string, CellSnapshot> = {}
  if (ranges.length === 0) return cells

  try {
    const result = await callAppsScript<{
      success: boolean
      cells: Record<string, { value: unknown; formula: string | null }>
    }>('captureRanges', ranges)

    if (result.success && result.cells) {
      for (const [key, cell] of Object.entries(result.cells)) {
        cells[key] = {
          value: cell.value,
          formula: cell.formula,
          format: getDefaultFormat(),
        }
      }
    }
  } catch (error) {
    console.warn('[DeltaCapture] Failed to read ranges from Sheets', error)
  }

  return cells
}

/**
 * Capture active sheet cells via Apps Script.
 */
async function captureActiveSheetCells(): Promise<
  Record<string, CellSnapshot>
> {
  const cells: Record<string, CellSnapshot> = {}

  try {
    const result = await callAppsScript<{
      success: boolean
      cells: Record<string, { value: unknown; formula: string | null }>
    }>('captureActiveSheet')

    if (result.success && result.cells) {
      for (const [key, cell] of Object.entries(result.cells)) {
        cells[key] = {
          value: cell.value,
          formula: cell.formula,
          format: getDefaultFormat(),
        }
      }
    }
  } catch (error) {
    console.warn('[DeltaCapture] Failed to capture active sheet', error)
  }

  return cells
}

/**
 * Capture a delta before a tool executes in Google Sheets.
 */
export async function captureToolDeltaSheets(
  toolName: string,
  toolCallId: string,
  args: Record<string, unknown>
): Promise<ToolDelta | null> {
  try {
    const affected = getAffectedRanges(toolName, args)

    if (affected.strategy === 'none') {
      return null
    }

    let beforeCells: Record<string, CellSnapshot>
    let strategy: CaptureStrategy = affected.strategy

    if (affected.strategy === 'targeted' && affected.ranges.length > 0) {
      beforeCells = await readCellsFromRanges(affected.ranges)
    } else if (
      affected.strategy === 'sheet-scan' ||
      affected.strategy === 'full-snapshot'
    ) {
      beforeCells = await captureActiveSheetCells()
      strategy = 'sheet-scan'
    } else {
      beforeCells = {}
    }

    return {
      toolCallId,
      toolName,
      timestamp: Date.now(),
      beforeCells,
      affectedRanges: affected.ranges,
      strategy,
    }
  } catch (error) {
    console.warn(
      `[DeltaCapture] Failed to capture delta for ${toolName}`,
      error
    )
    return null
  }
}
